import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useStore } from '../store';
import type { Game } from '../types';
import {
  Trash2,
  ChevronUp,
  ChevronDown,
  Plus,
  Sparkles,
  X,
  Target,
  Trophy,
  Shield,
  CircleDot,
} from 'lucide-react';
import { useRipple } from '../lib/useRipple';
import { useTranslation } from '../i18n/useTranslation';

type SortField = 'date' | 'opponent' | 'competitionType' | 'savePercentage';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

interface GameWithGoals extends Game {
  goalCount: number;
}

export default function Dashboard() {
  const { t } = useTranslation();
  const [games, setGames] = useState<GameWithGoals[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [gameToDelete, setGameToDelete] = useState<GameWithGoals | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'date',
    direction: 'desc',
  });
  const [showNewFeatures, setShowNewFeatures] = useState(false);
  const navigate = useNavigate();
  const user = useStore((state) => state.user);
  const { createRipple } = useRipple();

  const fetchGames = async () => {
    if (!user) return;

    try {
      const q = query(collection(db, 'games'), where('userId', '==', user.uid));

      const querySnapshot = await getDocs(q);
      const gamesData = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          date: new Date(data.date),
          createdAt: new Date(data.createdAt),
          goalCount: 0, // Initialize with 0, will be updated with actual count
        };
      }) as GameWithGoals[];

      // Fetch goal counts for each game
      const gamesWithGoals = await Promise.all(
        gamesData.map(async (game) => {
          const goalsQuery = query(
            collection(db, 'goals'),
            where('gameId', '==', game.id)
          );
          const goalsSnapshot = await getDocs(goalsQuery);
          return {
            ...game,
            goalCount: goalsSnapshot.docs.length,
          };
        })
      );

      setGames(gamesWithGoals);
    } catch (err) {
      console.error('Error fetching games:', err);
      setError('Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGames();
  }, [user]);

  const handleDeleteGame = async (gameId: string) => {
    if (!user) return;

    try {
      // Delete all goals associated with the game
      const goalsQuery = query(
        collection(db, 'goals'),
        where('gameId', '==', gameId)
      );
      const goalsSnapshot = await getDocs(goalsQuery);
      const deleteGoalsPromises = goalsSnapshot.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deleteGoalsPromises);

      // Delete the game
      await deleteDoc(doc(db, 'games', gameId));

      // Refresh games list
      await fetchGames();
      setGameToDelete(null);
    } catch (err) {
      console.error('Error deleting game:', err);
      setError('Failed to delete game');
    }
  };

  const handleSort = (field: SortField) => {
    setSortConfig((prevConfig) => ({
      field,
      direction:
        prevConfig.field === field && prevConfig.direction === 'asc'
          ? 'desc'
          : 'asc',
    }));
  };

  const getSortedGames = () => {
    const sortedGames = [...games];

    sortedGames.sort((a, b) => {
      switch (sortConfig.field) {
        case 'date':
          return sortConfig.direction === 'asc'
            ? a.date.getTime() - b.date.getTime()
            : b.date.getTime() - a.date.getTime();

        case 'opponent':
          return sortConfig.direction === 'asc'
            ? a.opponent.localeCompare(b.opponent)
            : b.opponent.localeCompare(a.opponent);

        case 'competitionType':
          return sortConfig.direction === 'asc'
            ? a.competitionType.localeCompare(b.competitionType)
            : b.competitionType.localeCompare(a.competitionType);

        case 'savePercentage': {
          const getSavePercentage = (game: GameWithGoals) => {
            if (game.shotsFaced === null || game.saves === null) return -1;
            return (game.saves / game.shotsFaced) * 100;
          };
          return sortConfig.direction === 'asc'
            ? getSavePercentage(a) - getSavePercentage(b)
            : getSavePercentage(b) - getSavePercentage(a);
        }

        default:
          return 0;
      }
    });

    return sortedGames;
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortConfig.field !== field) {
      return (
        <ChevronUp className='h-4 w-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200' />
      );
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className='h-4 w-4 text-primary-600' />
    ) : (
      <ChevronDown className='h-4 w-4 text-primary-600' />
    );
  };

  if (loading) {
    return (
      <div className='max-w-7xl mx-auto px-4 py-8'>
        <div className='text-center py-16'>
          <div className='glass-card max-w-md mx-auto p-8'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4'></div>
            <p className='text-slate-600 font-medium'>{t('common.loadingYourGames')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='max-w-7xl mx-auto px-4 py-8'>
        <div className='glass-card max-w-2xl mx-auto p-6 border-red-200/30 bg-red-50/40'>
          <div className='text-red-700 font-medium text-center'>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-7xl mx-auto px-4 py-8 space-y-8'>
      {/* Header Section */}
      <div className='glass-card p-6'>
        <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4'>
          <div>
            <h1 className='text-3xl font-bold text-primary-600 mb-2 italic'>
              {t('nav.games')}
            </h1>
            <p className='text-muted'>
              {t('dashboard.trackPerformance')}
            </p>
          </div>
          <div className='flex items-center gap-3'>
            <button
              onClick={(e) => {
                createRipple(e);
                setShowNewFeatures(true);
              }}
              className='btn-new-feature btn-ripple animate-shake group relative overflow-hidden'
            >
              {/* Enhanced glow effect */}
              <div className='absolute inset-0 bg-gradient-to-r from-emerald-400 to-emerald-300 animate-pulse opacity-50'></div>
              <div className='absolute -inset-1 bg-gradient-to-r from-emerald-400 to-emerald-300 blur-lg animate-pulse opacity-75'></div>
              <div className='absolute -inset-2 bg-gradient-to-r from-emerald-400 to-emerald-300 blur-xl animate-pulse opacity-50 group-hover:opacity-75 transition-opacity duration-300'></div>
              {/* Sparkle effect */}
              <div className='absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(255,255,255,0.4)_0%,rgba(255,255,255,0)_60%)] animate-pulse'></div>
              {/* Content */}
              <Sparkles className='h-5 w-5 relative animate-bounce text-white drop-shadow-lg' />
              <span className='hidden sm:inline relative font-medium drop-shadow-lg'>
                {t('dashboard.newFeatures')}
              </span>
            </button>
            <Link to='/new-game' className='btn-mui-primary btn-ripple group'>
              <span className='hidden sm:inline'>{t('dashboard.recordNewGame')}</span>
              <span className='sm:hidden'>
                <Plus className='h-5 w-5' />
              </span>
            </Link>
          </div>
        </div>
      </div>

      {/* Games Stats Cards */}
      {games.length > 0 && (
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
          <div className='metric-card group hover:scale-105 transition-transform duration-300'>
            <div className='flex items-center justify-center w-12 h-12 bg-primary-100/50 rounded-full mx-auto mb-3 group-hover:bg-primary-200/50 transition-colors duration-300'>
              <CircleDot className='h-6 w-6 text-primary-600' />
            </div>
            <div className='text-2xl font-bold text-heading mb-1'>
              {games.length}
            </div>
            <div className='text-sm text-muted'>{t('dashboard.totalGames')}</div>
          </div>
          <div className='metric-card group hover:scale-105 transition-transform duration-300'>
            <div className='flex items-center justify-center w-12 h-12 bg-emerald-100/50 rounded-full mx-auto mb-3 group-hover:bg-emerald-200/50 transition-colors duration-300'>
              <Trophy className='h-6 w-6 text-emerald-600' />
            </div>
            <div className='text-2xl font-bold text-heading mb-1'>
              {
                games.filter((g) => g.outcome && g.outcome.includes('win'))
                  .length
              }
            </div>
            <div className='text-sm text-muted'>Victoires</div>
          </div>
          <div className='metric-card group hover:scale-105 transition-transform duration-300'>
            <div className='flex items-center justify-center w-12 h-12 bg-purple-100/50 rounded-full mx-auto mb-3 group-hover:bg-purple-200/50 transition-colors duration-300'>
              <Shield className='h-6 w-6 text-purple-600' />
            </div>
            <div className='text-2xl font-bold text-heading mb-1'>
              {Math.round(
                (games.reduce((sum, game) => {
                  if (
                    game.saves !== null &&
                    game.shotsFaced !== null &&
                    game.shotsFaced > 0
                  ) {
                    return sum + game.saves / game.shotsFaced;
                  }
                  return sum;
                }, 0) /
                  games.filter(
                    (g) =>
                      g.saves !== null &&
                      g.shotsFaced !== null &&
                      g.shotsFaced > 0
                  ).length) *
                  100
              ) || 0}
              %
            </div>
            <div className='text-sm text-muted'>{t('dashboard.averageSavePercentage')}</div>
          </div>
        </div>
      )}

      {/* Games Table */}
      {games.length === 0 ? (
        <div className='glass-card p-12 text-center'>
          <div className='animate-float'>
            <Target className='h-16 w-16 text-slate-300 mx-auto mb-4' />
          </div>
          <h3 className='text-xl font-semibold text-heading mb-2'>
            {t('dashboard.noGames')}
          </h3>
          <p className='text-muted mb-6'>
            Start tracking your performance by recording your first game
          </p>
          <Link to='/new-game' className='btn-mui-primary btn-ripple'>
            <Plus className='h-5 w-5 mr-2' />
            Record Your First Game
          </Link>
        </div>
      ) : (
        <div className='table-glass'>
          <div className='overflow-x-auto'>
            <table className='min-w-full'>
              <thead className='table-header'>
                <tr>
                  <th className='px-6 py-4 text-left'>
                    <button
                      onClick={() => handleSort('date')}
                      className='group flex items-center space-x-1 text-sm font-semibold text-slate-700 hover:text-primary-600 transition-colors duration-200'
                    >
                      <span>{t('dashboard.date')}</span>
                      <SortIcon field='date' />
                    </button>
                  </th>
                  <th className='px-6 py-4 text-left'>
                    <button
                      onClick={() => handleSort('opponent')}
                      className='group flex items-center space-x-1 text-sm font-semibold text-slate-700 hover:text-primary-600 transition-colors duration-200'
                    >
                      <span>{t('dashboard.opponent')}</span>
                      <SortIcon field='opponent' />
                    </button>
                  </th>
                  <th className='px-6 py-4 text-left'>
                    <button
                      onClick={() => handleSort('competitionType')}
                      className='group flex items-center space-x-1 text-sm font-semibold text-slate-700 hover:text-primary-600 transition-colors duration-200'
                    >
                      <span>{t('dashboard.competition')}</span>
                      <SortIcon field='competitionType' />
                    </button>
                  </th>
                  <th className='px-6 py-4 text-left'>
                    <button
                      onClick={() => handleSort('savePercentage')}
                      className='group flex items-center space-x-1 text-sm font-semibold text-slate-700 hover:text-primary-600 transition-colors duration-200'
                    >
                      <span>{t('dashboard.savePercentage')}</span>
                      <SortIcon field='savePercentage' />
                    </button>
                  </th>
                  <th className='px-6 py-4 text-left text-sm font-semibold text-slate-700'>
                    {t('dashboard.goals')}
                  </th>
                  <th className='px-6 py-4 text-right text-sm font-semibold text-slate-700'>
                    {t('dashboard.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {getSortedGames().map((game, index) => (
                  <tr
                    key={game.id}
                    className='table-row group cursor-pointer'
                    onClick={() => navigate(`/game/${game.id}`)}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <td className='px-6 py-4 text-sm text-body'>
                      {game.date.toLocaleDateString()}
                    </td>
                    <td className='px-6 py-4'>
                      <div className='text-sm font-medium text-heading'>
                        {game.opponent}
                      </div>
                    </td>
                    <td className='px-6 py-4'>
                      <span className='status-indicator'>
                        {game.competitionType}
                      </span>
                    </td>
                    <td className='px-6 py-4 text-sm text-body'>
                      {game.shotsFaced !== null &&
                      game.saves !== null &&
                      game.shotsFaced > 0 ? (
                        <div className='flex items-center space-x-2'>
                          <span
                            className={`font-semibold ${
                              game.saves / game.shotsFaced >= 0.9
                                ? 'text-emerald-600'
                                : game.saves / game.shotsFaced >= 0.8
                                ? 'text-primary-600'
                                : 'text-amber-600'
                            }`}
                          >
                            {((game.saves / game.shotsFaced) * 100).toFixed(1)}%
                          </span>
                          <span className='text-muted text-xs'>
                            ({game.saves}/{game.shotsFaced})
                          </span>
                        </div>
                      ) : (
                        <span className='text-muted text-sm'>{t('common.notRecorded')}</span>
                      )}
                    </td>
                    <td className='px-6 py-4 text-sm text-body'>
                      {game.goalCount}
                    </td>
                    <td className='px-6 py-4 text-right'>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          createRipple(e);
                          setGameToDelete(game);
                        }}
                        className='btn-glass p-2 text-red-600 hover:text-red-700 hover:bg-red-50/50'
                      >
                        <Trash2 className='h-4 w-4' />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {gameToDelete && (
        <div className='fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50'>
          <div className='glass-modal max-w-md w-full p-6'>
            <h3 className='text-lg font-semibold text-heading mb-2'>
              Delete Game
            </h3>
            <p className='text-body mb-6'>
              Are you sure you want to delete the game against{' '}
              <span className='font-medium'>{gameToDelete.opponent}</span>? This
              action cannot be undone.
            </p>
            <div className='flex justify-end space-x-3'>
              <button
                onClick={(e) => {
                  createRipple(e);
                  setGameToDelete(null);
                }}
                className='btn-mui-secondary btn-ripple'
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  createRipple(e);
                  handleDeleteGame(gameToDelete.id);
                }}
                className='btn-mui-primary btn-ripple bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 border-red-600/20'
              >
                Delete Game
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Features Modal */}
      {showNewFeatures && (
        <div className='fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 z-50 overflow-y-auto'>
          <div className='glass-modal max-w-2xl w-full p-6 my-8'>
            <div className='flex justify-between items-center mb-6'>
              <h2 className='text-2xl font-bold text-gradient'>
                🎉 New Features!
              </h2>
              <button
                onClick={(e) => {
                  createRipple(e);
                  setShowNewFeatures(false);
                }}
                className='btn-glass p-2'
              >
                <X className='h-5 w-5' />
              </button>
            </div>

            <div className='space-y-6'>
              <div className='glass-card p-4 border-primary-200/30 bg-primary-50/30'>
                <h3 className='font-semibold text-primary-800 mb-2'>
                  ✨ Modern UI Design
                </h3>
                <p className='text-primary-700 text-sm'>
                  Experience our beautiful new glassmorphism design with
                  enhanced animations and modern typography.
                </p>
              </div>

              <div className='glass-card p-4 border-primary-200/30 bg-primary-50/30'>
                <h3 className='font-semibold text-primary-800 mb-2'>
                  🎯 Enhanced Analytics
                </h3>
                <p className='text-primary-700 text-sm'>
                  Get deeper insights into your performance with improved charts
                  and statistics visualization.
                </p>
              </div>

              <div className='glass-card p-4 border-purple-200/30 bg-purple-50/30'>
                <h3 className='font-semibold text-purple-800 mb-2'>
                  🚀 Performance Improvements
                </h3>
                <p className='text-purple-700 text-sm'>
                  Enjoy faster loading times and smoother interactions
                  throughout the app.
                </p>
              </div>
            </div>

            <div className='mt-8 text-center'>
              <button
                onClick={(e) => {
                  createRipple(e);
                  setShowNewFeatures(false);
                }}
                className='btn-mui-primary btn-ripple'
              >
                Awesome, Let's Go! 🎯
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}